import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { ArrowRight, Calendar, Clock } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';

/**
 * Bloco "Do nosso blog" — exibe os 3 posts publicados mais recentes na home
 * para aumentar páginas/visita e melhorar SEO interno.
 */
const BlogHighlights: React.FC = () => {
  const { data: posts, isLoading } = useQuery({
    queryKey: ['blog-highlights-home'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('blog_posts')
        .select('id, slug, title, excerpt, featured_image_url, featured_image_alt, published_at, read_time_minutes')
        .eq('status', 'published')
        .lte('published_at', new Date().toISOString())
        .order('published_at', { ascending: false })
        .limit(3);
      if (error) throw error;
      return data;
    },
    staleTime: 5 * 60 * 1000,
  });

  if (!isLoading && (!posts || posts.length === 0)) return null;

  return (
    <section
      aria-labelledby="blog-highlights-heading"
      className="py-12 md:py-20 px-4 md:px-6 bg-background"
    >
      <div className="container mx-auto max-w-6xl">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8 md:mb-12">
          <div>
            <p className="text-xs md:text-sm tracking-widest font-semibold uppercase text-primary mb-2">
              Conteúdo Tax Health
            </p>
            <h2
              id="blog-highlights-heading"
              className="text-2xl md:text-4xl font-bold text-foreground"
            >
              Aprenda sobre <span className="text-primary">equiparação hospitalar</span>
            </h2>
            <p className="text-sm md:text-base text-muted-foreground mt-2 max-w-2xl">
              Guias, cases e estratégias de tributação para médicos, clínicas e hospitais.
            </p>
          </div>
          <Link to="/blog" className="hidden md:inline-flex">
            <Button variant="outline">
              Ver todos os artigos
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>

        <div className="grid gap-6 md:grid-cols-3">
          {isLoading
            ? Array.from({ length: 3 }).map((_, i) => (
                <Card key={i} className="overflow-hidden">
                  <Skeleton className="aspect-video w-full" />
                  <CardContent className="p-5 space-y-3">
                    <Skeleton className="h-4 w-1/3" />
                    <Skeleton className="h-5 w-full" />
                    <Skeleton className="h-4 w-2/3" />
                  </CardContent>
                </Card>
              ))
            : posts!.map((post) => (
                <Link
                  key={post.id}
                  to={`/blog/${post.slug}`}
                  className="group focus:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-lg"
                >
                  <Card className="overflow-hidden h-full transition-shadow hover:shadow-lg">
                    <div className="aspect-video w-full bg-muted overflow-hidden">
                      {post.featured_image_url ? (
                        <img
                          src={post.featured_image_url}
                          alt={post.featured_image_alt || post.title}
                          loading="lazy"
                          decoding="async"
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          width={640}
                          height={360}
                        />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-primary/10 to-accent" />
                      )}
                    </div>
                    <CardContent className="p-5 space-y-3">
                      <div className="flex items-center gap-3 text-xs text-muted-foreground">
                        {post.published_at && (
                          <span className="inline-flex items-center gap-1">
                            <Calendar className="h-3.5 w-3.5" />
                            {format(new Date(post.published_at), "d 'de' MMM, yyyy", { locale: ptBR })}
                          </span>
                        )}
                        {post.read_time_minutes ? (
                          <span className="inline-flex items-center gap-1">
                            <Clock className="h-3.5 w-3.5" />
                            {post.read_time_minutes} min
                          </span>
                        ) : null}
                      </div>
                      <h3 className="text-base md:text-lg font-semibold text-foreground leading-snug group-hover:text-primary transition-colors line-clamp-2">
                        {post.title}
                      </h3>
                      {post.excerpt && (
                        <p className="text-sm text-muted-foreground line-clamp-2">{post.excerpt}</p>
                      )}
                      <span className="inline-flex items-center gap-1 text-sm font-medium text-primary">
                        Ler artigo
                        <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                      </span>
                    </CardContent>
                  </Card>
                </Link>
              ))}
        </div>

        <div className="mt-8 md:hidden text-center">
          <Link to="/blog">
            <Button variant="outline" className="w-full">
              Ver todos os artigos
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default BlogHighlights;
